// The app's half of loading a page's blog elements: the same requirements the
// renderer fulfils from the tables (api/code/src/render/resolve.blogOf),
// fulfilled through GraphQL for the editor's frame and the read-only preview.
// What is needed is decided once, in @common/render/blog; only the fetch is
// here, and the shape it returns is the shape the composer takes.
import _get from "lodash/get";
import { requirements } from "@common/render/blog";
import { sectionUnavailable } from "@common/render/page";
import * as BlogQL from "queries/blog";
import * as BlogPublishedQL from "queries/blog-published";
import * as BlogCollectionQL from "queries/blog-collection";

const SITE = "data.viewer.account.sites.get";

const fulfil = async (client, siteId, need) => {
  if (need.kind === "post") {
    if (!need.slug) {
      return { post: null };
    }

    const res = await client.query({
      query: BlogPublishedQL.bySlug,
      variables: { siteId, slug: need.slug },
    });

    return { post: _get(res, `${SITE}.blog.published.bySlug`) || null };
  }

  if (need.kind === "list") {
    const res = await client.query({
      query: BlogPublishedQL.page,
      variables: { siteId, input: { page: need.page, limit: need.limit, tag: need.tag } },
    });

    return { page: _get(res, `${SITE}.blog.published.list`) || null };
  }

  if (!need.collectionId) {
    return { collection: null, items: [] };
  }

  const res = await client.query({
    query: BlogCollectionQL.get,
    variables: { siteId, collectionId: need.collectionId },
  });
  const collection = _get(res, `${SITE}.blog.collections.get`);

  if (!collection) {
    return { collection: null, items: [] };
  }

  const rows = await Promise.all(
    (collection.postIds || []).map((postId) =>
      client
        .query({ query: BlogPublishedQL.get, variables: { siteId, postId } })
        .then((r) => _get(r, `${SITE}.blog.published.get`) || null)
    )
  );

  return { collection, items: rows.filter(Boolean) };
};

// The site's three templates, keyed by kind the way the composer reads them
// (templateFrom in @common/render/blog). The API resolves them against the
// built-ins, so a kind nobody has written arrives as the built-in and the
// composer never needs to know which is which.
export const templatesByKind = (rows) =>
  (rows || []).reduce((out, row) => {
    if (row && row.kind) {
      out[row.kind] = { html: row.html || "", style: row.style || "", script: row.script || "" };
    }
    return out;
  }, {});

export const fetchBlogData = async (client, siteId, page, params = {}) => {
  const needs = requirements(page, params);
  const data = { authors: [], templates: {}, elements: {} };

  if (needs.length === 0) {
    return data;
  }

  const [authorsResult, templatesResult, ...loaded] = await Promise.all([
    client
      .query({ query: BlogQL.authors, variables: { siteId } })
      .then((res) => _get(res, `${SITE}.blog.authors`) || [])
      .catch(() => []),
    // Empty on a failed read, and the composer renders the built-ins — a
    // preview with the default look is better than a blank one, and the
    // element's own read failing is what says so in place.
    client
      .query({ query: BlogQL.templates, variables: { siteId } })
      .then((res) => templatesByKind(_get(res, `${SITE}.blog.templates`)))
      .catch(() => ({})),
    ...needs.map((need) =>
      fulfil(client, siteId, need).catch((error) => sectionUnavailable(error))
    ),
  ]);

  data.authors = authorsResult;
  data.templates = templatesResult;
  needs.forEach((need, index) => {
    data.elements[need.elementid] = loaded[index];
  });

  return data;
};
